import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import ControlsSection from "../../components/ControlsSection";
import RefreshButton from "../../components/RefreshButton";
import {
  outlinedButtonSx,
  primaryButtonSx,
} from "../../../Organization/CodeSecurity/codeSecurityShared";
import {
  CONTROL_VISIBILITY,
  DEFAULT_CONTROL_VISIBILITY,
  controlsOfShownFrameworks,
  visibleControls,
} from "../../data/controls";
import {
  DialogHeader,
  dialogActionsSx,
  dialogPaperProps,
} from "../../components/DialogParts";

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

// "Which controls do visitors see?" One choice for the whole Trust Center.
const VisibilityDialog = ({ open, value, onClose, onSave }) => {
  const theme = useTheme();
  const [choice, setChoice] = useState(value);

  useEffect(() => {
    if (open) setChoice(value);
  }, [open, value]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={dialogPaperProps}
    >
      <DialogHeader
        title="Control visibility"
        subtitle="Choose which controls visitors see, and whether they see each control's status."
        onClose={onClose}
      />
      <DialogContent dividers>
        <RadioGroup
          aria-label="Control visibility"
          value={choice}
          onChange={(e) => setChoice(e.target.value)}
        >
          {Object.entries(CONTROL_VISIBILITY).map(([key, option]) => (
            <FormControlLabel
              key={key}
              value={key}
              control={<Radio />}
              sx={{ alignItems: "flex-start", mb: 1.5, mr: 0 }}
              label={
                <Box sx={{ pt: 1 }}>
                  <Typography sx={{ fontWeight: 500 }}>
                    {option.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: theme.customColors.text.secondary }}
                  >
                    {option.description}
                  </Typography>
                </Box>
              }
            />
          ))}
        </RadioGroup>
      </DialogContent>
      <DialogActions sx={dialogActionsSx(theme)}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={outlinedButtonSx(theme)}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={choice === value}
          onClick={() => {
            onSave(choice);
            onClose();
          }}
          sx={primaryButtonSx(theme)}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Controls tab: controls are live from Regodit and are not edited here. The
// admin chooses which of them visitors see, and sees the result below.
// Only controls of the frameworks shown on the Compliance tab count:
// hiding a framework there hides its controls here and on the page.
const ControlsTab = ({ controls, frameworks, visibility, onChange }) => {
  const theme = useTheme();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const mode = CONTROL_VISIBILITY[visibility]
    ? visibility
    : DEFAULT_CONTROL_VISIBILITY;

  const inScope = useMemo(
    () => controlsOfShownFrameworks(controls, frameworks),
    [controls, frameworks]
  );
  const shown = useMemo(
    () => visibleControls(controls, mode, frameworks),
    [controls, mode, frameworks]
  );
  const shownFrameworks = (frameworks || []).filter((f) => f.isVisible);
  const count = (status) => inScope.filter((c) => c.status === status).length;
  const hidden = inScope.length - shown.length;

  return (
    <Stack spacing={2}>
      <Card
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: "12px",
          border: `1px solid ${theme.customColors.border.main}`,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems="flex-start"
          justifyContent="space-between"
        >
          {/* The text takes the room left; the buttons keep their size. */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600 }}>
              {CONTROL_VISIBILITY[mode].label}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: theme.customColors.text.secondary, mt: 0.25 }}
            >
              {CONTROL_VISIBILITY[mode].description}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: theme.customColors.text.secondary, mt: 1 }}
            >
              Right now: {plural(count("pass"), "control")} passing,{" "}
              {count("fail")} failing, {count("unmapped")} with no checks
              mapped. Visitors see {shown.length} of {inScope.length}
              {hidden > 0 ? ` (${hidden} hidden)` : ""}.
            </Typography>
            <Typography
              variant="body2"
              aria-label="Frameworks the controls come from"
              sx={{ color: theme.customColors.text.secondary, mt: 0.5 }}
            >
              {shownFrameworks.length > 0
                ? `Controls of the frameworks shown on your page: ${shownFrameworks
                    .map((f) => f.name)
                    .join(
                      ", "
                    )}. Hide a framework on the Compliance tab to hide its controls.`
                : "No framework is shown on your page, so visitors see no controls. Show a framework on the Compliance tab."}
            </Typography>
          </Box>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ flexShrink: 0 }}
          >
            {/* Status is live: pull the latest check results. */}
            <RefreshButton label="Controls" />
            <Button
              variant="outlined"
              startIcon={<TuneOutlinedIcon sx={{ fontSize: 18 }} />}
              onClick={() => setSettingsOpen(true)}
              sx={{
                ...outlinedButtonSx(theme),
                flexShrink: 0,
                whiteSpace: "nowrap",
              }}
            >
              Visibility settings
            </Button>
          </Stack>
        </Stack>
      </Card>

      <Typography
        variant="body2"
        sx={{ color: theme.customColors.text.secondary }}
      >
        Below is the Controls tab as visitors see it. Status comes live from
        Regodit; fix failing controls on the Controls page.
      </Typography>
      {shown.length > 0 ? (
        <ControlsSection controls={shown} />
      ) : (
        <Card
          elevation={0}
          sx={{
            p: 3,
            textAlign: "center",
            borderRadius: "12px",
            border: `1px solid ${theme.customColors.border.main}`,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: theme.customColors.text.secondary }}
          >
            {shownFrameworks.length === 0
              ? "No framework is shown on your page, so no controls are shown."
              : "No controls are shown with this setting."}
          </Typography>
        </Card>
      )}

      <VisibilityDialog
        open={settingsOpen}
        value={mode}
        onClose={() => setSettingsOpen(false)}
        onSave={(next) => onChange({ controlVisibility: next })}
      />
    </Stack>
  );
};

export default ControlsTab;
